
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type TitleEntry = { owner: string; title: string };

type ShellContextValue = {
  pageTitle: string | null;
  immersive: boolean;
  registerTitle: (owner: string, title: string) => void;
  releaseTitle: (owner: string) => void;
  registerImmersive: (owner: string) => void;
  releaseImmersive: (owner: string) => void;
};

const ShellContext = createContext<ShellContextValue | null>(null);

export function ShellProvider({ children }: { children: ReactNode }) {
  const [titles, setTitles] = useState<TitleEntry[]>([]);
  const [immersiveOwners, setImmersiveOwners] = useState<string[]>([]);

  const registerTitle = useCallback((owner: string, title: string) => {
    setTitles((current) => [...current.filter((entry) => entry.owner !== owner), { owner, title }]);
  }, []);
  const releaseTitle = useCallback((owner: string) => {
    setTitles((current) => current.filter((entry) => entry.owner !== owner));
  }, []);
  const registerImmersive = useCallback((owner: string) => {
    setImmersiveOwners((current) => (current.includes(owner) ? current : [...current, owner]));
  }, []);
  const releaseImmersive = useCallback((owner: string) => {
    setImmersiveOwners((current) => current.filter((entry) => entry !== owner));
  }, []);

  const value = useMemo<ShellContextValue>(
    () => ({
      pageTitle: titles.at(-1)?.title ?? null,
      immersive: immersiveOwners.length > 0,
      registerTitle,
      releaseTitle,
      registerImmersive,
      releaseImmersive,
    }),
    [titles, immersiveOwners, registerTitle, releaseTitle, registerImmersive, releaseImmersive],
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShellState(): Pick<ShellContextValue, "pageTitle" | "immersive"> {
  const context = useContext(ShellContext);
  return { pageTitle: context?.pageTitle ?? null, immersive: context?.immersive ?? false };
}

export function usePageTitle(title: string | null | undefined) {
  const context = useContext(ShellContext);
  const owner = useId();
  const register = context?.registerTitle;
  const release = context?.releaseTitle;

  useEffect(() => {
    if (!register || !release || !title) return;
    register(owner, title);
    return () => release(owner);
  }, [owner, register, release, title]);
}

export function useImmersiveMode(active: boolean) {
  const context = useContext(ShellContext);
  const owner = useId();
  const register = context?.registerImmersive;
  const release = context?.releaseImmersive;

  useEffect(() => {
    if (!register || !release || !active) return;
    register(owner);
    return () => release(owner);
  }, [active, owner, register, release]);
}
